import { escapeForRegEx } from '@tiptap/core';

export interface LegalRefEntry {
  /** Canonical citation, e.g. "Reg. CE 852/2004". */
  id: string;
  /** Short Italian description shown in the hover card. */
  title: string;
  /** Official institutional source only: EUR-Lex (IT) or Normattiva. */
  url: string;
  /** Recognized textual variants. Must include the canonical form. */
  aliases: string[];
}

const eurLex = (celex: string) =>
  `https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:${celex}`;

const normattiva = (tipo: string, dataNum: string) =>
  `https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:${tipo}:${dataNum}`;

// The matcher regex is case-insensitive, so aliases only need structural
// variants (parentheses, "n.", extended wording), not case variants.
function euRegAliases(tipo: 'CE' | 'UE', num: string): string[] {
  const englishMarker = tipo === 'CE' ? 'EC' : 'EU';
  return [
    ...englishRegAliases(englishMarker, num),
    `Reg. ${tipo} ${num}`,
    `Reg. ${tipo} n. ${num}`,
    `Reg. (${tipo}) ${num}`,
    `Reg. (${tipo}) n. ${num}`,
    `Regolamento ${tipo} ${num}`,
    `Regolamento ${tipo} n. ${num}`,
    `Regolamento (${tipo}) ${num}`,
    `Regolamento (${tipo}) n. ${num}`,
    // Forms without the CE/UE marker: number + year is unique per act.
    `Reg. ${num}`,
    `Reg. n. ${num}`,
    `Regolamento ${num}`,
  ];
}

// Survey and manual copy is authored in IT and EN: the EN text cites the same
// acts with the EC/EU marker, which the IT-only aliases never matched.
function englishRegAliases(marker: 'EC' | 'EU', num: string): string[] {
  return [
    `Reg. ${marker} ${num}`,
    `Reg. (${marker}) ${num}`,
    `Reg. (${marker}) No ${num}`,
    `Regulation (${marker}) ${num}`,
    `Regulation (${marker}) No ${num}`,
  ];
}

function dlgsAliases(num: string, dataEstesa: string): string[] {
  const [n, anno] = num.split('/');
  const breve = `${n}/${anno.slice(-2)}`;
  return [
    `D.Lgs. ${num}`,
    `D.Lgs. n. ${num}`,
    `D.Lgs ${num}`,
    `D. Lgs. ${num}`,
    `D.Lgs. ${breve}`,
    `D.Lgs. n. ${breve}`,
    `Decreto Legislativo ${num}`,
    `Decreto Legislativo n. ${num}`,
    `Decreto Legislativo ${dataEstesa}, n. ${n}`,
  ];
}

export const LEGAL_REFERENCES: LegalRefEntry[] = [
  // --- Unione Europea (EUR-Lex) ---
  {
    id: 'Reg. CE 178/2002',
    title:
      'Principi generali della legislazione alimentare: sicurezza, rintracciabilità, ritiro e richiamo; istituisce l’EFSA.',
    url: eurLex('32002R0178'),
    aliases: euRegAliases('CE', '178/2002'),
  },
  {
    id: 'Reg. CE 852/2004',
    title:
      'Igiene dei prodotti alimentari: obbligo di procedure basate sui principi HACCP per tutti gli OSA.',
    url: eurLex('32004R0852'),
    aliases: euRegAliases('CE', '852/2004'),
  },
  {
    id: 'Reg. CE 853/2004',
    title:
      'Norme specifiche di igiene per gli alimenti di origine animale (carni, latticini, uova, prodotti della pesca).',
    url: eurLex('32004R0853'),
    aliases: euRegAliases('CE', '853/2004'),
  },
  {
    id: 'Reg. CE 854/2004',
    title:
      'Controlli ufficiali sui prodotti di origine animale — abrogato, sostituito dal Reg. UE 2017/625.',
    url: eurLex('32004R0854'),
    aliases: euRegAliases('CE', '854/2004'),
  },
  {
    id: 'Reg. CE 882/2004',
    title: 'Controlli ufficiali su mangimi e alimenti — abrogato, sostituito dal Reg. UE 2017/625.',
    url: eurLex('32004R0882'),
    aliases: euRegAliases('CE', '882/2004'),
  },
  {
    id: 'Reg. UE 2017/625',
    title:
      'Controlli ufficiali lungo la filiera agroalimentare; abroga i Reg. CE 854/2004 e 882/2004.',
    url: eurLex('32017R0625'),
    aliases: euRegAliases('UE', '2017/625'),
  },
  {
    id: 'Reg. UE 1169/2011',
    title:
      'Informazioni sugli alimenti ai consumatori: etichettatura, allergeni (Allegato II), dichiarazione nutrizionale.',
    url: eurLex('32011R1169'),
    aliases: euRegAliases('UE', '1169/2011'),
  },
  {
    id: 'Reg. UE 1276/2011',
    title:
      'Trattamento per uccidere i parassiti vitali nei prodotti della pesca destinati al consumo crudo (congelamento anti-Anisakis).',
    url: eurLex('32011R1276'),
    aliases: euRegAliases('UE', '1276/2011'),
  },
  {
    id: 'Reg. UE 142/2011',
    title:
      'Disposizioni di applicazione del Reg. CE 1069/2009 sui sottoprodotti di origine animale.',
    url: eurLex('32011R0142'),
    aliases: euRegAliases('UE', '142/2011'),
  },
  {
    id: 'Racc. 2003/361/CE',
    title:
      'Raccomandazione della Commissione relativa alla definizione delle microimprese, piccole e medie imprese.',
    url: eurLex('32003H0361'),
    aliases: [
      'Racc. 2003/361/CE',
      'Raccomandazione 2003/361/CE',
      'Raccomandazione della Commissione 2003/361/CE',
      'raccomandazione 2003/361',
    ],
  },
  {
    id: 'Reg. CE 2073/2005',
    title: 'Criteri microbiologici applicabili ai prodotti alimentari.',
    url: eurLex('32005R2073'),
    aliases: euRegAliases('CE', '2073/2005'),
  },
  {
    id: 'Reg. CE 1935/2004',
    title: 'Materiali e oggetti destinati a venire a contatto con i prodotti alimentari (MOCA).',
    url: eurLex('32004R1935'),
    aliases: euRegAliases('CE', '1935/2004'),
  },
  {
    id: 'Reg. UE 10/2011',
    title:
      'Materiali e oggetti di materia plastica destinati al contatto con alimenti (MOCA plastica).',
    url: eurLex('32011R0010'),
    aliases: euRegAliases('UE', '10/2011'),
  },
  {
    id: 'Reg. UE 2020/1245',
    title:
      'Modifica e rettifica del Reg. UE 10/2011 sui materiali e oggetti di materia plastica destinati al contatto con gli alimenti (MOCA).',
    url: eurLex('32020R1245'),
    aliases: euRegAliases('UE', '2020/1245'),
  },
  {
    id: 'Reg. CE 2023/2006',
    title: 'Buone pratiche di fabbricazione (GMP) dei materiali a contatto con alimenti.',
    url: eurLex('32006R2023'),
    aliases: euRegAliases('CE', '2023/2006'),
  },
  {
    id: 'Reg. UE 2022/1616',
    title: 'Materiali e oggetti di plastica riciclata destinati al contatto con gli alimenti.',
    url: eurLex('32022R1616'),
    aliases: euRegAliases('UE', '2022/1616'),
  },
  {
    id: 'Reg. UE 2021/382',
    title:
      'Modifica il Reg. CE 852/2004: gestione degli allergeni nei prerequisiti e cultura della sicurezza alimentare.',
    url: eurLex('32021R0382'),
    aliases: euRegAliases('UE', '2021/382'),
  },
  {
    id: 'Reg. CE 1881/2006',
    title:
      'Tenori massimi di contaminanti negli alimenti — abrogato, sostituito dal Reg. UE 2023/915.',
    url: eurLex('32006R1881'),
    aliases: euRegAliases('CE', '1881/2006'),
  },
  {
    id: 'Reg. UE 2023/915',
    title:
      'Livelli massimi di alcuni contaminanti nei prodotti alimentari (sostituisce il Reg. CE 1881/2006).',
    url: eurLex('32023R0915'),
    aliases: euRegAliases('UE', '2023/915'),
  },
  {
    id: 'Reg. CE 37/2005',
    title:
      'Controllo delle temperature nei mezzi di trasporto e nei depositi di alimenti surgelati.',
    url: eurLex('32005R0037'),
    aliases: euRegAliases('CE', '37/2005'),
  },
  {
    id: 'Comunicazione 2022/C 355/01',
    title:
      'Orientamenti UE su GHP e procedure HACCP, con flessibilità per le microimprese (sostituisce 2016/C 278/01 e 2020/C 199/01).',
    url: 'https://eur-lex.europa.eu/legal-content/IT/ALL/?uri=CELEX:52022XC0916(01)',
    aliases: [
      'Comunicazione 2022/C 355/01',
      'Comunicazione UE 2022/C 355/01',
      'Comunicazione (UE) 2022/C 355/01',
      'Comunicazione della Commissione 2022/C 355/01',
      '2022/C 355/01',
    ],
  },
  {
    id: 'Reg. UE 2015/2283',
    title: 'Nuovi alimenti (novel food): condizioni di immissione sul mercato UE.',
    url: eurLex('32015R2283'),
    aliases: euRegAliases('UE', '2015/2283'),
  },
  {
    id: 'Reg. CE 1924/2006',
    title: 'Indicazioni nutrizionali e sulla salute fornite sui prodotti alimentari (claims).',
    url: eurLex('32006R1924'),
    aliases: euRegAliases('CE', '1924/2006'),
  },
  {
    id: 'Reg. UE 2016/429',
    title: 'Normativa UE in materia di sanità animale (Animal Health Law).',
    url: eurLex('32016R0429'),
    aliases: euRegAliases('UE', '2016/429'),
  },
  {
    id: 'Reg. CE 1760/2000',
    title: 'Identificazione e registrazione dei bovini ed etichettatura delle carni bovine.',
    url: eurLex('32000R1760'),
    aliases: euRegAliases('CE', '1760/2000'),
  },
  {
    id: 'Reg. CE 1069/2009',
    title:
      'Norme sanitarie sui sottoprodotti di origine animale non destinati al consumo umano (SOA).',
    url: eurLex('32009R1069'),
    aliases: euRegAliases('CE', '1069/2009'),
  },
  {
    id: 'Reg. CE 1333/2008',
    title: 'Additivi alimentari: elenco UE e condizioni d’uso.',
    url: eurLex('32008R1333'),
    aliases: euRegAliases('CE', '1333/2008'),
  },
  {
    id: 'Reg. CE 1334/2008',
    title: 'Aromi e ingredienti alimentari con proprietà aromatizzanti.',
    url: eurLex('32008R1334'),
    aliases: euRegAliases('CE', '1334/2008'),
  },
  {
    id: 'Reg. UE 2018/775',
    title:
      'Indicazione dell’origine dell’ingrediente primario (attuazione art. 26 Reg. UE 1169/2011).',
    url: eurLex('32018R0775'),
    aliases: euRegAliases('UE', '2018/775'),
  },
  {
    id: 'Reg. UE 2019/627',
    title:
      'Modalità pratiche dei controlli ufficiali sui prodotti di origine animale (attuazione Reg. UE 2017/625).',
    url: eurLex('32019R0627'),
    aliases: euRegAliases('UE', '2019/627'),
  },
  {
    id: 'Direttiva 98/83/CE',
    title:
      'Qualità delle acque destinate al consumo umano — abrogata, sostituita dalla Direttiva UE 2020/2184.',
    url: eurLex('31998L0083'),
    aliases: ['Direttiva 98/83/CE', 'Dir. 98/83/CE', 'Direttiva 98/83', 'Dir. 98/83'],
  },
  {
    id: 'Direttiva UE 2020/2184',
    title:
      'Qualità delle acque destinate al consumo umano (rifusione; recepita in Italia dal D.Lgs. 18/2023).',
    url: eurLex('32020L2184'),
    aliases: [
      'Direttiva UE 2020/2184',
      'Direttiva (UE) 2020/2184',
      'Dir. UE 2020/2184',
      'Dir. (UE) 2020/2184',
    ],
  },
  {
    id: 'Direttiva 2011/91/UE',
    title:
      'Diciture o marche che consentono di identificare la partita (lotto) delle derrate alimentari.',
    url: eurLex('32011L0091'),
    aliases: ['Direttiva 2011/91/UE', 'Dir. 2011/91/UE', 'Direttiva 2011/91', 'Dir. 2011/91'],
  },
  {
    id: 'Reg. CE 2160/2003',
    title: 'Controllo della salmonella e di altri agenti zoonotici trasmessi dagli alimenti.',
    url: eurLex('32003R2160'),
    aliases: euRegAliases('CE', '2160/2003'),
  },
  {
    id: 'Reg. UE 528/2012',
    title:
      'Messa sul mercato e uso dei biocidi: disinfettanti, prodotti per il controllo delle infestanti (BPR).',
    url: eurLex('32012R0528'),
    aliases: euRegAliases('UE', '528/2012'),
  },
  {
    id: 'Reg. CE 1907/2006',
    title:
      'Registrazione, valutazione e autorizzazione delle sostanze chimiche (REACH); schede di dati di sicurezza.',
    url: eurLex('32006R1907'),
    aliases: euRegAliases('CE', '1907/2006'),
  },
  {
    id: 'Reg. CE 1272/2008',
    title:
      'Classificazione, etichettatura e imballaggio delle sostanze e miscele chimiche pericolose (CLP).',
    url: eurLex('32008R1272'),
    aliases: euRegAliases('CE', '1272/2008'),
  },
  {
    id: 'Reg. UE 2017/2158',
    title:
      'Misure di mitigazione dell’acrilamide nei prodotti alimentari (patatine, pane, caffè, ecc.).',
    url: eurLex('32017R2158'),
    aliases: euRegAliases('UE', '2017/2158'),
  },
  {
    id: 'Reg. UE 2025/40',
    title:
      'Imballaggi e rifiuti di imballaggio (PPWR): riciclabilità, etichettatura armonizzata degli imballaggi.',
    url: eurLex('32025R0040'),
    aliases: euRegAliases('UE', '2025/40'),
  },
  {
    id: 'Reg. UE 2024/2895',
    title:
      'Modifica il Reg. CE 2073/2005 per il criterio di sicurezza alimentare su Listeria monocytogenes negli alimenti pronti al consumo.',
    url: eurLex('32024R2895'),
    aliases: euRegAliases('UE', '2024/2895'),
  },
  {
    id: 'Reg. UE 2016/679',
    title:
      'Protezione dei dati personali (GDPR): trattamenti su dipendenti, clienti e sistemi informatici.',
    url: eurLex('32016R0679'),
    aliases: euRegAliases('UE', '2016/679'),
  },
  {
    id: 'Reg. UE 2024/1689',
    title:
      'Regolamento sull’intelligenza artificiale (AI Act): obblighi per i sistemi AI, inclusi quelli degli strumenti digitali.',
    url: eurLex('32024R1689'),
    aliases: euRegAliases('UE', '2024/1689'),
  },
  {
    id: 'Intesa Stato-Regioni 50/CSR del 5 maggio 2021',
    title:
      'Linee guida per la gestione operativa del sistema di allerta per alimenti, mangimi e materiali a contatto con gli alimenti (sostituisce l’intesa del 13 novembre 2008).',
    url: 'https://www.statoregioni.it/it/conferenza-stato-regioni/sedute-2021/seduta-del-05052021/atti/repertorio-atto-n-50csr/',
    aliases: [
      'Intesa Stato-Regioni 50/CSR del 5 maggio 2021',
      'Intesa Stato-Regioni del 5 maggio 2021',
      'Intesa Stato-Regioni 5 maggio 2021',
      'Intesa Stato-Regioni Rep. Atti n. 50/CSR',
      'Intesa Stato-Regioni Rep. Atti 50/CSR',
      'Rep. Atti n. 50/CSR del 5 maggio 2021',
      'Rep. Atti n. 50/CSR',
      '50/CSR del 5 maggio 2021',
    ],
  },
  {
    id: 'Accordo Stato-Regioni 29 aprile 2010',
    title:
      'Linee guida applicative del Reg. (CE) 852/2004 sull’igiene dei prodotti alimentari: contenuti minimi della formazione degli alimentaristi (Rep. Atti n. 59/CSR).',
    url: 'https://www.gazzettaufficiale.it/eli/id/2010/05/26/10A06350/sg',
    aliases: [
      'Accordo Stato-Regioni 29 aprile 2010',
      'Accordo Stato-Regioni del 29 aprile 2010',
      'Intesa Stato-Regioni 29 aprile 2010',
      'Accordo Stato-Regioni Rep. Atti n. 59/CSR',
      'Rep. Atti n. 59/CSR del 29 aprile 2010',
      'Rep. Atti n. 59/CSR',
    ],
  },
  // --- Italia (Normattiva) ---
  {
    id: 'L. 241/1990',
    title:
      'Procedimento amministrativo; l’art. 19 disciplina la segnalazione certificata di inizio attività (SCIA).',
    url: normattiva('legge', '1990-08-07;241'),
    aliases: [
      'L. 241/1990',
      'L. n. 241/1990',
      'Legge 241/1990',
      'Legge n. 241/1990',
      'Legge 7 agosto 1990, n. 241',
    ],
  },
  {
    id: 'D.P.R. 160/2010',
    title: 'Regolamento dello Sportello unico per le attività produttive (SUAP).',
    url: normattiva('decreto.del.presidente.della.repubblica', '2010-09-07;160'),
    aliases: [
      'D.P.R. 160/2010',
      'D.P.R. n. 160/2010',
      'DPR 160/2010',
      'DPR n. 160/2010',
      'Decreto del Presidente della Repubblica 7 settembre 2010, n. 160',
    ],
  },
  {
    id: 'L. 283/1962',
    title:
      'Disciplina igienica della produzione e vendita di sostanze alimentari e bevande (parzialmente vigente).',
    url: normattiva('legge', '1962-04-30;283'),
    aliases: [
      'L. 283/1962',
      'L. n. 283/1962',
      'Legge 283/1962',
      'Legge n. 283/1962',
      'Legge 283/62',
      'Legge 30 aprile 1962, n. 283',
    ],
  },
  {
    id: 'DPR 327/1980',
    title: 'Regolamento di esecuzione della L. 283/1962 (parzialmente vigente).',
    url: normattiva('decreto.del.presidente.della.repubblica', '1980-03-26;327'),
    aliases: [
      'DPR 327/1980',
      'DPR n. 327/1980',
      'D.P.R. 327/1980',
      'D.P.R. n. 327/1980',
      'DPR 327/80',
      'Decreto del Presidente della Repubblica 26 marzo 1980, n. 327',
    ],
  },
  {
    id: 'D.L. 69/2013',
    title:
      'Decreto “del fare”: l’art. 42, comma 7-bis (conv. L. 98/2013) abroga l’art. 14 della L. 283/1962 e l’art. 37 del DPR 327/1980, eliminando a livello nazionale l’obbligo del libretto di idoneità sanitaria per gli alimentaristi.',
    url: normattiva('decreto.legge', '2013-06-21;69'),
    aliases: [
      'D.L. 69/2013',
      'DL 69/2013',
      'D.L. n. 69/2013',
      'Decreto-Legge 69/2013',
      'Decreto Legge 69/2013',
      'Decreto-Legge 21 giugno 2013, n. 69',
    ],
  },
  {
    id: 'L. 98/2013',
    title: 'Legge di conversione, con modificazioni, del D.L. 69/2013 (decreto “del fare”).',
    url: normattiva('legge', '2013-08-09;98'),
    aliases: [
      'L. 98/2013',
      'L. n. 98/2013',
      'Legge 98/2013',
      'Legge n. 98/2013',
      'Legge 9 agosto 2013, n. 98',
    ],
  },
  {
    id: 'D.Lgs. 109/1992',
    title:
      'Etichettatura, presentazione e pubblicità dei prodotti alimentari — in gran parte abrogato dal D.Lgs. 231/2017.',
    url: normattiva('decreto.legislativo', '1992-01-27;109'),
    aliases: dlgsAliases('109/1992', '27 gennaio 1992'),
  },
  {
    id: 'D.Lgs. 110/1992',
    title:
      'Alimenti surgelati destinati all’alimentazione umana (attuazione direttiva 89/108/CEE).',
    url: normattiva('decreto.legislativo', '1992-01-27;110'),
    aliases: dlgsAliases('110/1992', '27 gennaio 1992'),
  },
  {
    id: 'D.Lgs. 517/1992',
    title:
      'Attuazione della direttiva 71/319/CEE sugli strumenti per pesare a funzionamento non automatico: obblighi di verificazione periodica e sanzioni per gli strumenti non conformi.',
    url: normattiva('decreto.legislativo', '1992-12-29;517'),
    aliases: [...dlgsAliases('517/1992', '29 dicembre 1992'), 'D.Lgs 29 dicembre 1992, n. 517'],
  },
  {
    id: 'D.Lgs. 190/2006',
    title:
      'Disciplina sanzionatoria per le violazioni del Reg. CE 178/2002 (rintracciabilità, ritiro, richiamo).',
    url: normattiva('decreto.legislativo', '2006-04-05;190'),
    aliases: dlgsAliases('190/2006', '5 aprile 2006'),
  },
  {
    id: 'D.Lgs. 193/2007',
    title:
      'Regime sanzionatorio del pacchetto igiene e controlli in materia di sicurezza alimentare.',
    url: normattiva('decreto.legislativo', '2007-11-06;193'),
    aliases: dlgsAliases('193/2007', '6 novembre 2007'),
  },
  {
    id: 'D.Lgs. 29/2017',
    title: 'Disciplina sanzionatoria sui materiali e oggetti a contatto con alimenti (MOCA).',
    url: normattiva('decreto.legislativo', '2017-02-10;29'),
    aliases: dlgsAliases('29/2017', '10 febbraio 2017'),
  },
  {
    id: 'D.Lgs. 231/2017',
    title:
      'Disciplina sanzionatoria per le violazioni del Reg. UE 1169/2011 (etichettatura e allergeni).',
    url: normattiva('decreto.legislativo', '2017-12-15;231'),
    aliases: dlgsAliases('231/2017', '15 dicembre 2017'),
  },
  {
    id: 'D.Lgs. 27/2021',
    title: 'Adeguamento della normativa nazionale al Reg. UE 2017/625 sui controlli ufficiali.',
    url: normattiva('decreto.legislativo', '2021-02-02;27'),
    aliases: dlgsAliases('27/2021', '2 febbraio 2021'),
  },
  {
    id: 'D.Lgs. 32/2021',
    title:
      'Finanziamento dei controlli ufficiali sulla filiera agroalimentare ex Reg. UE 2017/625.',
    url: normattiva('decreto.legislativo', '2021-02-02;32'),
    aliases: dlgsAliases('32/2021', '2 febbraio 2021'),
  },
  {
    id: 'D.Lgs. 31/2001',
    title:
      'Qualità delle acque destinate al consumo umano (attuazione Direttiva 98/83/CE) — abrogato dal D.Lgs. 18/2023.',
    url: normattiva('decreto.legislativo', '2001-02-02;31'),
    aliases: dlgsAliases('31/2001', '2 febbraio 2001'),
  },
  {
    id: 'D.Lgs. 18/2023',
    title: 'Qualità delle acque destinate al consumo umano (attuazione Direttiva UE 2020/2184).',
    url: normattiva('decreto.legislativo', '2023-02-23;18'),
    aliases: dlgsAliases('18/2023', '23 febbraio 2023'),
  },
  {
    id: 'D.Lgs. 102/2025',
    title:
      'Disposizioni integrative e correttive del D.Lgs. 18/2023 in materia di qualità delle acque destinate al consumo umano.',
    url: normattiva('decreto.legislativo', '2025-06-19;102'),
    aliases: dlgsAliases('102/2025', '19 giugno 2025'),
  },
  {
    id: 'DM 93/2017',
    title:
      'Verificazione periodica degli strumenti per pesare a funzionamento non automatico (bilance commerciali) e degli altri strumenti di misura legale.',
    url: 'https://www.normattiva.it/uri-res/N2Ls?urn:nir:ministero.sviluppo.economico:decreto:2017-04-21;93',
    aliases: [
      'DM 93/2017',
      'D.M. 93/2017',
      'DM n. 93/2017',
      'DM 21 aprile 2017 n. 93',
      'DM 21 aprile 2017, n. 93',
      'D.M. 21 aprile 2017, n. 93',
      'Decreto Ministeriale 21 aprile 2017, n. 93',
    ],
  },
  {
    id: 'D.M. 25/2012',
    title:
      'Disposizioni tecniche sulle apparecchiature per il trattamento dell’acqua destinata al consumo umano (addolcitori, filtri).',
    url: 'https://www.normattiva.it/uri-res/N2Ls?urn:nir:ministero.salute:decreto:2012-02-07;25',
    aliases: [
      'D.M. 25/2012',
      'DM 25/2012',
      'D.M. n. 25/2012',
      'D.M. 7 febbraio 2012, n. 25',
      'Decreto Ministeriale 25/2012',
      'Decreto Ministeriale 7 febbraio 2012, n. 25',
    ],
  },
  {
    id: 'D.Lgs. 179/2021',
    title: 'Disciplina sanzionatoria per le violazioni del Reg. (UE) 528/2012 sui biocidi.',
    url: normattiva('decreto.legislativo', '2021-11-02;179'),
    aliases: dlgsAliases('179/2021', '2 novembre 2021'),
  },
  {
    id: 'L. 82/1994',
    title:
      'Disciplina delle attività di pulizia, disinfezione, disinfestazione, derattizzazione e sanificazione: iscrizione delle imprese al Registro delle imprese.',
    url: normattiva('legge', '1994-01-25;82'),
    aliases: [
      'L. 82/1994',
      'L. n. 82/1994',
      'Legge 82/1994',
      'Legge n. 82/1994',
      'Legge 82/94',
      'Legge 25 gennaio 1994, n. 82',
    ],
  },
  {
    id: 'DM 274/1997',
    title:
      'Regolamento di attuazione della L. 82/1994: requisiti tecnico-professionali delle imprese di pulizia, disinfestazione e derattizzazione.',
    url: 'https://www.gazzettaufficiale.it/eli/id/1997/08/13/097G0306/sg',
    aliases: [
      'DM 274/1997',
      'D.M. 274/1997',
      'DM n. 274/1997',
      'D.M. n. 274/1997',
      'Decreto Ministeriale 274/1997',
      'Decreto Ministeriale 7 luglio 1997, n. 274',
    ],
  },
  {
    id: 'D.Lgs. 81/2008',
    title:
      'Testo unico sulla salute e sicurezza sul lavoro: formazione, DPI, sorveglianza sanitaria.',
    url: normattiva('decreto.legislativo', '2008-04-09;81'),
    aliases: dlgsAliases('81/2008', '9 aprile 2008'),
  },
  {
    id: 'D.Lgs. 152/2006',
    title:
      'Testo unico ambientale: rifiuti, scarichi ed emissioni (incluso lo smaltimento degli oli alimentari esausti).',
    url: normattiva('decreto.legislativo', '2006-04-03;152'),
    aliases: dlgsAliases('152/2006', '3 aprile 2006'),
  },
  {
    id: 'D.M. 59/2023',
    title:
      'Regolamento RENTRI: tracciabilità digitale dei rifiuti (registri cronologici, formulari, applicazione del sistema elettronico nazionale).',
    url: 'https://www.normattiva.it/uri-res/N2Ls?urn:nir:ministero.ambiente.e.sicurezza.energetica:decreto:2023-04-04;59',
    aliases: [
      'D.M. 59/2023',
      'DM 59/2023',
      'D.M. n. 59/2023',
      'D.M. 4 aprile 2023, n. 59',
      'Decreto Ministeriale 59/2023',
      'Decreto Ministeriale 4 aprile 2023, n. 59',
    ],
  },
  // --- Norme tecniche UNI (store ufficiale UNI) ---
  {
    id: 'UNI EN 13697',
    title:
      'Metodo di prova quantitativo dell’attività battericida e levuricida dei disinfettanti su superfici non porose.',
    url: 'https://store.uni.com/uni-en-13697-2023',
    aliases: ['UNI EN 13697', 'UNI EN 13697:2019', 'UNI EN 13697:2023', 'EN 13697'],
  },
  {
    id: 'UNI EN 12830',
    title:
      'Requisiti e prove dei registratori di temperatura per il trasporto, il deposito e la distribuzione di alimenti refrigerati, congelati, surgelati/congelati e dei gelati.',
    url: 'https://store.uni.com/uni-en-12830-2018',
    aliases: ['UNI EN 12830', 'UNI EN 12830:2018', 'EN 12830'],
  },
  {
    id: 'UNI EN 13485',
    title:
      'Requisiti e prove dei termometri per il trasporto, il deposito e la distribuzione di alimenti refrigerati, congelati, surgelati/congelati e dei gelati.',
    url: 'https://store.uni.com/uni-en-13485-2024',
    aliases: ['UNI EN 13485', 'UNI EN 13485:2024', 'EN 13485'],
  },
  {
    id: 'UNI EN 13486',
    title:
      'Procedura di verifica periodica dei registratori di temperatura e dei termometri per il trasporto, il deposito e la distribuzione di alimenti refrigerati, congelati, surgelati/congelati e dei gelati.',
    url: 'https://store.uni.com/uni-en-13486-2024',
    aliases: ['UNI EN 13486', 'UNI EN 13486:2024', 'EN 13486'],
  },
  {
    id: 'ILAC-G24 / OIML D10',
    title:
      'Linee guida internazionali (ILAC/OIML) per la determinazione degli intervalli di ricalibrazione degli strumenti di misura.',
    url: 'https://www.oiml.org/en/files/pdf_d/d010-e22.pdf',
    aliases: ['ILAC-G24 / OIML D10', 'ILAC-G24', 'OIML D10', 'OIML D 10'],
  },
  {
    id: 'UNI EN 1276',
    title:
      'Metodo di prova dell’attività battericida dei disinfettanti chimici in ambito alimentare.',
    url: 'https://store.uni.com/uni-en-1276-2019',
    aliases: ['UNI EN 1276', 'UNI EN 1276:2019', 'EN 1276'],
  },
  {
    id: 'UNI EN 15780',
    title:
      'Pulizia degli impianti di ventilazione: criteri di valutazione e mantenimento nel tempo della pulizia, con allegato dedicato alla pulizia di cappe e condotti delle cucine professionali.',
    url: 'https://store.uni.com/uni-en-15780-2025',
    aliases: ['UNI EN 15780', 'UNI EN 15780:2025', 'EN 15780'],
  },
  {
    id: 'UNI EN 16282',
    title:
      'Impianti di ventilazione per cucine commerciali: requisiti e prove per cappe, condotti e componenti dell’impianto di aspirazione.',
    url: 'https://store.uni.com/uni-en-16282-1-2026',
    aliases: ['UNI EN 16282', 'UNI EN 16282-1', 'UNI EN 16282-1:2026', 'EN 16282', 'EN 16282-1'],
  },
  {
    id: 'UNI EN 16636',
    title:
      'Servizi di gestione e controllo delle infestazioni (pest management): requisiti e competenze dei fornitori professionali.',
    url: 'https://store.uni.com/uni-en-16636-2015',
    aliases: ['UNI EN 16636', 'UNI EN 16636:2015', 'EN 16636'],
  },
  {
    id: 'UNI 10847',
    title:
      'Criteri e procedure per la pulizia e il controllo dei sistemi fumari a servizio di generatori e apparecchi alimentati con combustibili liquidi e solidi (canne fumarie di combustione).',
    url: 'https://store.uni.com/uni-10847-2017',
    aliases: ['UNI 10847', 'UNI 10847:2017'],
  },
  {
    id: 'D.M. 3 settembre 2021',
    title:
      'Criteri generali di progettazione, realizzazione ed esercizio della sicurezza antincendio per i luoghi di lavoro (D.Lgs. 81/2008, art. 46).',
    url: 'https://www.gazzettaufficiale.it/eli/id/2021/10/29/21A06349/sg',
    aliases: [
      'D.M. 3 settembre 2021',
      'Decreto Ministeriale 3 settembre 2021',
      'DM 3 settembre 2021',
      'D.M. del 3 settembre 2021',
    ],
  },
];

const normalize = (s: string) => s.toLowerCase().replace(/\s+/g, ' ');

const entryByAlias = new Map<string, LegalRefEntry>();
const entryById = new Map<string, LegalRefEntry>();
const patterns: string[] = [];
for (const entry of LEGAL_REFERENCES) {
  entryById.set(entry.id, entry);
  for (const alias of entry.aliases) {
    entryByAlias.set(normalize(alias), entry);
    patterns.push(escapeForRegEx(alias).replace(/ /g, '\\s+'));
  }
}
// Longest-first: JS alternation takes the FIRST matching branch, not the longest.
patterns.sort((a, b) => b.length - a.length);
// Lookarounds behave like \b plus a "/" guard, so "852/2004" never matches
// inside "1852/2004" or "852/2004/17".
const LEGAL_REF_REGEX = new RegExp(`(?<![\\w/])(?:${patterns.join('|')})(?![\\w/])`, 'gi');

export interface LegalRefMatch {
  start: number;
  end: number;
  entry: LegalRefEntry;
}

export function findLegalRefMatches(text: string): LegalRefMatch[] {
  const matches: LegalRefMatch[] = [];
  for (const match of text.matchAll(LEGAL_REF_REGEX)) {
    const entry = entryByAlias.get(normalize(match[0]));
    if (entry) {
      matches.push({ start: match.index, end: match.index + match[0].length, entry });
    }
  }
  return matches;
}

export function getLegalRefById(id: string): LegalRefEntry | undefined {
  return entryById.get(id);
}

/** Human name of the institutional source behind the entry URL. */
export function legalRefSourceLabel(url: string): string {
  if (url.includes('eur-lex.europa.eu')) {
    return 'EUR-Lex';
  }
  if (url.includes('normattiva.it')) {
    return 'Normattiva';
  }
  // Conferenza Stato-Regioni: fonte istituzionale verificata per l'Intesa
  // 50/CSR del 5 maggio 2021 (round 16, decisione 10) — non EUR-Lex né
  // Normattiva, che non pubblicano gli atti della Conferenza.
  if (url.includes('statoregioni.it')) {
    return 'Conferenza Stato-Regioni';
  }
  // Gazzetta Ufficiale: fonte istituzionale per il D.M. 3 settembre 2021, un
  // decreto ministeriale non presente su Normattiva con un proprio URN.
  if (url.includes('gazzettaufficiale.it')) {
    return 'Gazzetta Ufficiale';
  }
  // OIML: fonte istituzionale per la guida ILAC-G24 / OIML D10 sugli
  // intervalli di ricalibrazione, pubblicata dall'International Organization
  // of Legal Metrology — non è una norma UNI.
  if (url.includes('oiml.org')) {
    return 'OIML';
  }
  return 'UNI';
}
